import React, { useEffect } from 'react';
import { fetchCourseList } from '../redux/actions/Actions';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { 
  Avatar, 
  Box, 
  Card, 
  CardContent, 
  CardMedia, 
  Chip, 
  Container, 
  Grid, 
  Typography, 
  Button,
  Divider,
  useTheme
} from '@mui/material';
import {
  Assignment,
  Bookmark,
  Star,
  Schedule,
  School,
  TrendingUp
} from '@mui/icons-material';
import { styled } from '@mui/system';

// Styled components
const HeroSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 0),
  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
  color: theme.palette.common.white,
  marginBottom: theme.spacing(4),
}));

const CourseCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.shadows[8],
  },
}));

const CourseMedia = styled(Box)(({ theme }) => ({
  height: 160,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.primary.light,
  color: theme.palette.primary.contrastText,
}));

const Courses = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const fetchCourses = useSelector((state) => state.fetchAllCourses.courses);

  useEffect(() => {
    dispatch(fetchCourseList());
  }, [dispatch]);

  return (
    <Box sx={{ backgroundColor: 'background.default' }}>
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="lg">
          <Box textAlign="center">
            <Chip 
              label="Browse Our Catalog" 
              color="secondary" 
              sx={{ 
                mb: 2,
                color: 'white',
                fontWeight: 600,
                fontSize: '0.875rem'
              }} 
            />
            <Typography 
              variant="h2" 
              component="h1" 
              gutterBottom 
              sx={{ 
                fontWeight: 700,
                color: theme.palette.common.white
              }}
            >
              Our Courses
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                maxWidth: '700px', 
                margin: '0 auto',
                opacity: 0.9
              }}
            >
              Master new skills with our industry-relevant courses taught by experts
            </Typography>
          </Box>
        </Container>
      </HeroSection>

      {/* Courses Grid */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={4}>
          {fetchCourses.map((course) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={course.course_id}>
              <CourseCard elevation={3}>
                <CourseMedia>
                  <Avatar
                    sx={{ 
                      width: 80, 
                      height: 80,
                      bgcolor: theme.palette.primary.dark,
                      fontSize: '2rem'
                    }}
                  >
                    <Assignment fontSize="large" />
                  </Avatar>
                </CourseMedia>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography 
                    gutterBottom 
                    variant="h6" 
                    component="h3"
                    sx={{
                      fontWeight: 600,
                      mb: 1,
                      height: '64px',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }}
                  >
                    <Link 
                      to={`/courses/${course.course_id}`} 
                      style={{ 
                        color: 'inherit', 
                        textDecoration: 'none',
                        '&:hover': {
                          color: theme.palette.primary.main
                        }
                      }}
                    >
                      {course.course_name}
                    </Link>
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Chip
                      icon={<Schedule fontSize="small" />}
                      label={`${course.duration || '4 Weeks'}`}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      icon={<School fontSize="small" />}
                      label={course.level || 'Beginner'}
                      size="small"
                      color="secondary"
                    />
                  </Box>
                  
                  <Divider sx={{ my: 1 }} />
                  
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 2
                  }}>
                    <Button
                      component={Link}
                      to={`/courses/${course.course_id}`}
                      size="small"
                      variant="outlined"
                      color="primary"
                      sx={{
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: '4px'
                      }}
                    >
                      View Details
                    </Button>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Star color="warning" fontSize="small" />
                      <Typography variant="body2" sx={{ ml: 0.5 }}>
                        {course.rating || '4.8'}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </CourseCard>
            </Grid>
          ))}
        </Grid>

        {/* Call to Action */}
        <Box sx={{ 
          textAlign: 'center', 
          mt: 8,
          p: 4,
          borderRadius: '8px',
          backgroundColor: theme.palette.grey[100]
        }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            Can't find what you're looking for?
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, maxWidth: '600px', margin: '0 auto' }}>
            We're constantly adding new courses. Contact us to suggest a course you'd like to see.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            sx={{
              px: 4,
              fontWeight: 600,
              borderRadius: '4px'
            }}
          >
            Contact Our Team
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default Courses;